import moment from 'moment';
import { useCallback } from 'react';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';
import { DownloadPdfType } from '@api/requests/applications/downloadApplication';
import ApplicationDetailsInterface from '@api/requests/applications/interfaces/ApplicationDetailsInterface.interface';

import { convertBase64ToBlob } from '@utils/convertBase64';
import downloadDocuments from '@utils/downloadDocuments';

function sanitizeFileNamePart(value: string) {
	return value
		.trim()
		.replace(/\s+/g, '_')
		.replace(/[^\w-]/g, '');
}

function getApplicantName(application: ApplicationDetailsInterface) {
	const primaryApplicant =
		application.applicants?.find(applicant => applicant.type === 'primary') ?? application.applicants?.[0];
	const details = primaryApplicant?.details;

	return {
		firstName: details?.first_name ?? details?.given_name,
		lastName: details?.last_name ?? details?.family_name,
	};
}

function buildTaxFormPdfFileName(application: ApplicationDetailsInterface) {
	const { firstName, lastName } = getApplicantName(application);

	const applicantName = [firstName, lastName]
		.filter(Boolean)
		.map(name => sanitizeFileNamePart(name as string))
		.join('_');

	const currentSystemDate = moment().format('YYYY-MM-DD');

	return `Tax_Form_${applicantName || 'Applicant'}_${currentSystemDate}.pdf`;
}

function buildApplicationPdfFileName(application: ApplicationDetailsInterface) {
	return `${[application.uid, moment().unix()].filter(Boolean).join('_')}.pdf`;
}

function buildPdfFileName(application: ApplicationDetailsInterface, type: DownloadPdfType) {
	if (type === 'tax-form') {
		return buildTaxFormPdfFileName(application);
	}

	return buildApplicationPdfFileName(application);
}

export function useDownloadApplicationPDF(
	application: ApplicationDetailsInterface,
	type: DownloadPdfType = 'application',
) {
	const { startRequest: download, isPending } = useTriggeredRequest(
		AccountClientRequests.Applications.downloadApplication,
	);

	const handleDownloadApplicationPDF = useCallback(async () => {
		const res = await download({
			application_id: application.id,
			type,
		});

		const base64Data = res.pdf.blob.split(',')[1];
		const pdfBlob = convertBase64ToBlob(base64Data, 'application/pdf');

		downloadDocuments(
			{
				fileStream: pdfBlob,
				contentType: 'application/pdf',
			},
			buildPdfFileName(application, type),
		);
	}, [application, download, type]);

	return {
		handleDownloadApplicationPDF,
		isApplicationPDFDownloading: isPending,
	};
}
